import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import httpService from "../services/httpService";
import eventBus from "../services/eventBus";

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import "../styles/dashbaords.scss";
import "../styles/listenerDiscussion.scss";
import LoadingFade from "../components/LoadingFade";
import PageHero from "../components/PageHero";
import UserAvatar from "../components/UserAvatar";
import { FaChevronLeft, FaReply, FaCommentAlt, FaPaperPlane, FaThumbsUp, FaHeart, FaLaughSquint, FaSadTear, FaAngry, FaEdit, FaSearch, FaTimes, FaSortAmountUp, FaSortAmountDown } from "react-icons/fa";
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';

const REACTION_CONFIG = [
    { type: 'like',  Icon: FaThumbsUp,    label: 'Like' },
    // { type: 'love',  Icon: FaHeart,        label: 'Love' },
    // { type: 'laugh', Icon: FaLaughSquint,  label: 'Laugh' },
    // { type: 'sad',   Icon: FaSadTear,      label: 'Sad' },
    // { type: 'angry', Icon: FaAngry,        label: 'Angry' },
];

const userName = (user) =>
    user?.name ?? [user?.first_name, user?.last_name].filter(Boolean).join(' ') ?? '—';

const PodcasterDiscussion = () => {
    const { guid, discussionGuid } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [discussion, setDiscussion] = useState(null);

    const [listeners, setListeners] = useState([]);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [activeParentComment, setActiveParentComment] = useState(null);
    const [commentBody, setCommentBody] = useState('');
    const [commentMentions, setCommentMentions] = useState([]);
    const [mentionQuery, setMentionQuery] = useState(null);
    const [submittingComment, setSubmittingComment] = useState(false);
    const [commentError, setCommentError] = useState(null);
    const [reactions, setReactions] = useState({});
    const [reactionCounts, setReactionCounts] = useState({});
    const textareaRef = useRef(null);

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('asc');
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [pagination, setPagination] = useState({ total: 0, totalPages: 1, hasNextPage: false });

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ title: '', content: '', is_pinned: false, status: 'hidden', allow_comments: true });
    const [savingDiscussion, setSavingDiscussion] = useState(false);
    const [editErrors, setEditErrors] = useState({});

    const openEditModal = () => {
        setEditForm({
            title: discussion.title || '',
            content: discussion.body || '',
            is_pinned: !!discussion.is_pinned,
            status: discussion.status || 'hidden',
            allow_comments: discussion.allow_comments !== false,
        });
        setEditErrors({});
        setShowEditModal(true);
    };

    const handleUpdateDiscussion = () => {
        setSavingDiscussion(true);
        setEditErrors({});
        httpService.put(`/dash/club/${guid}/discussions/${discussionGuid}`, editForm)
            .then((res) => {
                if (res.data.toast) eventBus.emit('toast', res.data.toast);
                setShowEditModal(false);
                fetchDiscussion();
            })
            .catch((err) => {
                setEditErrors(err.response?.data?.errors || {});
            })
            .finally(() => setSavingDiscussion(false));
    };

    const fetchDiscussion = (page = pageNumber, sortValue = sort, searchValue = search) => {
        const params = {
            pageNumber: page,
            pageSize,
            sort: sortValue,
            ...(searchValue && { search: searchValue }),
        };
        httpService.get(`/dash/club/${guid}/discussions/${discussionGuid}`, { params })
            .then((res) => {
                const data = res.data.data ?? null;
                setDiscussion(data);
                setListeners(res.data.listeners || []);
                setPagination({
                    total: res.data.total || 0,
                    totalPages: res.data.totalPages || 1,
                    hasNextPage: res.data.hasNextPage || false,
                });
                const reactionMap = {};
                const countMap = {};
                const extractReactions = (list) => {
                    list?.forEach((c) => {
                        reactionMap[c.id] = c.my_reaction?.reaction_type || null;
                        countMap[c.id] = c.reactions || { like: 0, love: 0, laugh: 0, sad: 0, angry: 0 };
                        extractReactions(c.sub_comments);
                    });
                };
                extractReactions(data?.comments);
                setReactions(reactionMap);
                setReactionCounts(countMap);
            })
            .catch((err) => setError(err.response?.data?.message || 'Error loading discussion'))
            .finally(() => setLoading(false));
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setPageNumber(1);
        fetchDiscussion(1, sort, search);
    };

    const handleClearSearch = () => {
        setSearch('');
        setPageNumber(1);
        fetchDiscussion(1, sort, '');
    };

    useEffect(() => {
        fetchDiscussion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guid, discussionGuid, pageNumber, pageSize, sort]);

    const openCommentModal = (parentComment = null) => {
        setActiveParentComment(parentComment);
        setCommentBody('');
        setCommentMentions([]);
        setMentionQuery(null);
        setCommentError(null);
        setShowCommentModal(true);
        setTimeout(() => textareaRef.current?.focus(), 100);
    };

    const closeCommentModal = () => {
        setShowCommentModal(false);
        setActiveParentComment(null);
        setCommentBody('');
        setCommentMentions([]);
        setMentionQuery(null);
        setCommentError(null);
    };

    const handleCommentBodyChange = (e) => {
        const val = e.target.value;
        setCommentBody(val);
        const cursorPos = e.target.selectionStart;
        const textBeforeCursor = val.slice(0, cursorPos);
        const match = textBeforeCursor.match(/@(\w*)$/);
        if (match) {
            setMentionQuery(match[1].toLowerCase());
        } else {
            setMentionQuery(null);
        }
    };

    const handleSelectMention = (listener) => {
        const cursorPos = textareaRef.current.selectionStart;
        const textBeforeCursor = commentBody.slice(0, cursorPos);
        const textAfterCursor = commentBody.slice(cursorPos);
        const replaced = textBeforeCursor.replace(/@(\w*)$/, `@${listener.name} `);
        setCommentBody(replaced + textAfterCursor);
        setCommentMentions((prev) =>
            prev.includes(listener.guid) ? prev : [...prev, listener.guid]
        );
        setMentionQuery(null);
        setTimeout(() => textareaRef.current?.focus(), 0);
    };

    const filteredListeners = (mentionQuery !== null && mentionQuery !== '')
        ? listeners.filter((l) => {
            if (!l.name) return false;
            return l.name.toLowerCase().includes(mentionQuery);
        }).slice(0, 6)
        : [];

    const handleSubmitComment = () => {
        if (!commentBody.trim()) return;
        setSubmittingComment(true);
        setCommentError(null);
        httpService.post(`/dash/club/${guid}/discussions/${discussionGuid}/comments`, {
            body: commentBody.trim(),
            parent_comment_id: activeParentComment?.id ?? null,
            mentions: commentMentions.length > 0 ? commentMentions : null,
        })
            .then((res) => {
                if (res.data.toast) {
                    eventBus.emit('toast', res.data.toast);
                }
                closeCommentModal();
                fetchDiscussion();
            })
            .catch((err) => {
                setCommentError(
                    err.response?.data?.message ||
                    err.response?.data?.toast?.message ||
                    'Failed to post comment.'
                );
            })
            .finally(() => setSubmittingComment(false));
    };

    const handleReaction = (commentId, type) => {
        const prevType = reactions[commentId];
        httpService.post(`/dash/listener/discussions/comments/${commentId}/reaction`, {
            reaction_type: type,
        }).then((res) => {
            const newType = res.data.reaction?.reaction_type || null;
            setReactions((prev) => ({ ...prev, [commentId]: newType }));
            setReactionCounts((prev) => {
                const counts = { ...(prev[commentId] || {}) };
                if (prevType) counts[prevType] = Math.max(0, (counts[prevType] || 0) - 1);
                if (newType) counts[newType] = (counts[newType] || 0) + 1;
                return { ...prev, [commentId]: counts };
            });
        });
    };

    const renderSubComments = (subComments, depth = 1) => {
        if (!subComments?.length) return null;
        return (
            <div className="sub-comments mt-3">
                {subComments.map((reply) => (
                    <div key={reply.id} className="sub-comment d-flex gap-2 mt-2">
                        <FaReply className="sub-comment-icon mt-1 flex-shrink-0" />
                        <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-1 gap-sm-2">
                                    <span style={{ fontSize: '0.65rem' }} className="text-muted d-sm-none">
                                        {new Date(reply.created_at).toLocaleString()}
                                    </span>
                                    <div className="d-flex align-items-center gap-2">
                                        <UserAvatar user={reply.user} size={30} />
                                        <strong className="small">{userName(reply.user)}</strong>
                                    </div>
                                </div>
                                <small className="text-muted d-none d-sm-inline">
                                    {new Date(reply.created_at).toLocaleString()}
                                </small>
                            </div>
                            <p className="mb-0 small">{reply.body}</p>
                            <div className="mt-1 d-flex justify-content-between align-items-center">
                                <OverlayTrigger placement="top" overlay={<Tooltip>Reply</Tooltip>}>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        style={{ fontSize: '0.7rem', padding: '1px 6px' }}
                                        onClick={() => openCommentModal(reply)}
                                    >
                                        <FaCommentAlt />
                                    </Button>
                                </OverlayTrigger>
                                <div className="d-flex flex-wrap gap-1">
                                    {REACTION_CONFIG.map(({ type, Icon, label }) => {
                                        const count = reactionCounts[reply.id]?.[type] || 0;
                                        return (
                                            <OverlayTrigger key={type} placement="top" overlay={<Tooltip>{label}</Tooltip>}>
                                                <Button
                                                    variant={reactions[reply.id] === type ? 'primary' : 'outline-secondary'}
                                                    size="sm"
                                                    style={{ fontSize: '0.7rem', padding: '1px 6px' }}
                                                    onClick={() => handleReaction(reply.id, type)}
                                                >
                                                    <Icon />{count > 0 && <span className="ms-1">{count}</span>}
                                                </Button>
                                            </OverlayTrigger>
                                        );
                                    })}
                                </div>
                            </div>
                            {renderSubComments(reply.sub_comments, depth + 1)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'not-loading'}>

            {/* ── Hero ── */}
            <PageHero backgroundImage="/img/discussion-wide.jpg">
                {/* Back button */}
                <Button
                    variant="light"
                    size="sm"
                    className="ld-back-btn mb-3"
                    onClick={() => navigate(`/podcaster/podcast-club/${guid}`)}
                >
                    <FaChevronLeft className="me-1" /> Back to club
                </Button>

                {discussion && (
                    <div>
                        <h1 className="page-hero__title mb-1">{discussion.title}</h1>
                        {discussion.body && (
                            <p className="page-hero__sub mb-2">{discussion.body}</p>
                        )}
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                            {discussion.is_pinned === 1 && (
                                <Badge bg="warning" text="dark" style={{ fontSize: '0.7rem' }}>Pinned</Badge>
                            )}
                            <Badge
                                bg={discussion.status === 'published' ? 'success' : 'secondary'}
                                style={{ fontSize: '0.7rem' }}
                            >
                                {discussion.status}
                            </Badge>
                            <span className="ld-hero-meta">by {userName(discussion.user)}</span>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Discussion</Tooltip>}>
                                <Button variant="light" size="sm" className="" onClick={openEditModal}>
                                    <FaEdit /> edit
                                </Button>
                            </OverlayTrigger>
                            {discussion.allow_comments && (
                                <Button variant="primary" size="sm" onClick={() => openCommentModal()}>
                                    <FaCommentAlt className="me-1" /> Add Comment
                                </Button>
                            )}
                        </div>
                    </div>
                )}
            </PageHero>

            {/* ── Content ── */}
            <div className="container-lg py-4 px-3 px-lg-4">

                {error && <div className="alert alert-danger">{error}</div>}

                {discussion && (
                    <>
                        <Row className="mb-3 align-items-center">
                            <Col md={6}>
                                <Form onSubmit={handleSearchSubmit}>
                                    <div className="d-flex align-items-center gap-2">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="text"
                                                placeholder="Search comments..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                            <Button variant="primary" type="submit"><FaSearch /></Button>
                                        </InputGroup>
                                        {search && (
                                            <OverlayTrigger placement="right" overlay={<Tooltip>Clear search</Tooltip>}>
                                                <Button variant="outline-secondary" size="sm" onClick={handleClearSearch}>
                                                    <FaTimes />
                                                </Button>
                                            </OverlayTrigger>
                                        )}
                                        <OverlayTrigger placement="right" overlay={<Tooltip>{sort === 'asc' ? 'Oldest first' : 'Newest first'}</Tooltip>}>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setSort(s => s === 'asc' ? 'desc' : 'asc')}
                                            >
                                                {sort === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </Form>
                            </Col>
                            <Col md={6} className="d-flex align-items-center justify-content-end gap-2">
                                <Form.Label className="mb-0 small">Per page:</Form.Label>
                                <Form.Select size="sm" value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value)); setPageNumber(1); }} style={{ width: 'auto' }}>
                                    <option value="10">10</option>
                                    <option value="20">20</option>
                                    <option value="50">50</option>
                                </Form.Select>
                            </Col>
                        </Row>

                        {discussion.comments?.length > 0 ? (
                            discussion.comments.map((comment) => (
                                <Card key={comment.id} className="mb-3 discussion-comment">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <div className="d-flex align-items-center gap-2">
                                                <UserAvatar user={comment.user} size={40} />
                                                <strong>{userName(comment.user)}</strong>
                                            </div>
                                            <small className="text-muted">
                                                {new Date(comment.created_at).toLocaleString()}
                                            </small>
                                        </div>
                                        <p className="mb-0">{comment.body}</p>
                                        <div className="mt-2 d-flex justify-content-between align-items-center">
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Reply</Tooltip>}>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => openCommentModal(comment)}
                                                >
                                                    <FaCommentAlt />
                                                </Button>
                                            </OverlayTrigger>
                                            <div className="d-flex flex-wrap gap-1">
                                                {REACTION_CONFIG.map(({ type, Icon, label }) => {
                                                    const count = reactionCounts[comment.id]?.[type] || 0;
                                                    return (
                                                        <OverlayTrigger key={type} placement="top" overlay={<Tooltip>{label}</Tooltip>}>
                                                            <Button
                                                                variant={reactions[comment.id] === type ? 'primary' : 'outline-secondary'}
                                                                size="sm"
                                                                onClick={() => handleReaction(comment.id, type)}
                                                            >
                                                                <Icon />{count > 0 && <span className="ms-1">{count}</span>}
                                                            </Button>
                                                        </OverlayTrigger>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        {renderSubComments(comment.sub_comments)}
                                    </Card.Body>
                                </Card>
                            ))
                        ) : (
                            <p className="text-muted text-center my-4">No comments yet.</p>
                        )}

                        {pagination.total > 0 && (
                            <Row className="mt-3 align-items-center">
                                <Col>
                                    <small className="text-muted">{pagination.total} comment{pagination.total !== 1 ? 's' : ''} total</small>
                                </Col>
                                <Col className="d-flex justify-content-end align-items-center gap-2">
                                    <Button variant="outline-primary" size="sm" onClick={() => setPageNumber(p => p - 1)} disabled={pageNumber <= 1}>
                                        Previous
                                    </Button>
                                    <small>Page {pageNumber} of {pagination.totalPages}</small>
                                    <Button variant="outline-primary" size="sm" onClick={() => setPageNumber(p => p + 1)} disabled={!pagination.hasNextPage}>
                                        Next
                                    </Button>
                                </Col>
                            </Row>
                        )}
                    </>
                )}
            </div>

            <Modal show={showCommentModal} onHide={closeCommentModal} centered dialogClassName="ld-comment-modal">
                <Modal.Body className="p-0">

                    {/* Modal header — gradient bar */}
                    <div className="ld-modal__header">
                        <div>
                            <div className="ld-modal__label">
                                {activeParentComment ? 'Replying to' : 'New Comment'}
                            </div>
                            <div className="ld-modal__title">
                                {activeParentComment
                                    ? userName(activeParentComment.user)
                                    : discussion?.title}
                            </div>
                        </div>
                        <button className="ld-modal__close" onClick={closeCommentModal} aria-label="Close">
                            <FaTimes />
                        </button>
                    </div>

                    <div className="ld-modal__body">
                        {commentError && (
                            <div className="alert alert-danger py-2 mb-3 small">{commentError}</div>
                        )}

                        {/* Quoted parent when replying */}
                        {activeParentComment && (
                            <div className="ld-modal__quote">
                                <div className="ld-modal__quote-author">{userName(activeParentComment.user)}</div>
                                <div className="ld-modal__quote-body">{activeParentComment.body}</div>
                            </div>
                        )}

                        {/* Textarea + mention dropdown */}
                        <div className="position-relative">
                            <Form.Control
                                as="textarea"
                                rows={4}
                                className="ld-modal__textarea"
                                placeholder="Write your comment… Use @ to mention a listener"
                                value={commentBody}
                                onChange={handleCommentBodyChange}
                                ref={textareaRef}
                                disabled={submittingComment}
                            />
                            <div className="ld-modal__char-count">{commentBody.length} chars</div>
                            {filteredListeners.length > 0 && (
                                <div className="mention-dropdown border bg-white shadow-sm position-absolute w-100"
                                    style={{ zIndex: 1050, top: '100%', left: 0 }}>
                                    {filteredListeners.map((l) => (
                                        <div key={l.guid} className="mention-option px-3 py-2"
                                            onMouseDown={(e) => { e.preventDefault(); handleSelectMention(l); }}>
                                            {l.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Mention tags */}
                        {commentMentions.length > 0 && (
                            <div className="ld-modal__mentions">
                                <span className="ld-modal__mentions-label">Mentioning:</span>
                                {commentMentions.map((g) => {
                                    const l = listeners.find((x) => x.guid === g);
                                    return l ? (
                                        <span key={g} className="ld-modal__mention-tag">
                                            @{l.name}
                                            <button
                                                className="ld-modal__mention-remove"
                                                onClick={() => setCommentMentions((prev) => prev.filter((x) => x !== g))}
                                                aria-label={`Remove @${l.name}`}
                                            >
                                                <FaTimes />
                                            </button>
                                        </span>
                                    ) : null;
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="ld-modal__footer">
                        <span className="ld-modal__hint">
                            Use @ to mention · mentioned users are notified
                        </span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm"
                                onClick={closeCommentModal} disabled={submittingComment}>
                                Cancel
                            </Button>
                            <Button variant="primary" size="sm"
                                onClick={handleSubmitComment}
                                disabled={submittingComment || !commentBody.trim()}>
                                {submittingComment
                                    ? 'Posting…'
                                    : <><FaPaperPlane className="me-1" />Post Comment</>}
                            </Button>
                        </div>
                    </div>

                </Modal.Body>
            </Modal>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" backdrop="static" keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Discussion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                            isInvalid={!!editErrors.title}
                            disabled={savingDiscussion}
                        />
                        <Form.Control.Feedback type="invalid">{editErrors.title}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Content</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={5}
                            value={editForm.content}
                            onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                            isInvalid={!!editErrors.content}
                            disabled={savingDiscussion}
                        />
                        <Form.Control.Feedback type="invalid">{editErrors.content}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                            value={editForm.status}
                            onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                            disabled={savingDiscussion}
                        >
                            <option value="published">Published</option>
                            <option value="hidden">Hidden</option>
                            <option value="archived">Archived</option>
                            <option value="deleted">Deleted</option>
                        </Form.Select>
                    </Form.Group>
                    <div className="d-flex gap-4">
                        <Form.Check
                            type="switch"
                            id="edit-is-pinned"
                            label="Pinned"
                            checked={editForm.is_pinned}
                            onChange={(e) => setEditForm(prev => ({ ...prev, is_pinned: e.target.checked }))}
                            disabled={savingDiscussion}
                        />
                        <Form.Check
                            type="switch"
                            id="edit-allow-comments"
                            label="Allow Comments"
                            checked={editForm.allow_comments}
                            onChange={(e) => setEditForm(prev => ({ ...prev, allow_comments: e.target.checked }))}
                            disabled={savingDiscussion}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={savingDiscussion}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateDiscussion} disabled={savingDiscussion || !editForm.title.trim()}>
                        {savingDiscussion ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</> : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </LoadingFade>
    );
};

export default PodcasterDiscussion;
